import type { AreaForecast, WbgtTimeline } from '@heat-pwa/types';
import { bandForLevel } from '../wbgt/wbgtScale.js';
import { tempColor } from '../../lib/tempColor.js';

export type PopupPayload =
  | { kind: 'wbgt'; stationCode: string; lat: number; lng: number }
  | { kind: 'forecast'; spotCode: string; lat: number; lng: number };

export type RenderContext = {
  wbgt?: { timeline: WbgtTimeline | undefined; timestamp: string | undefined };
  forecast?: { forecasts: AreaForecast[]; date: string | undefined };
};

const ESC: Record<string, string> = {
  '&': '&amp;',
  '<': '&lt;',
  '>': '&gt;',
  '"': '&quot;',
  "'": '&#39;',
};

function escape(s: string): string {
  return s.replace(/[&<>"']/g, (ch) => ESC[ch] ?? ch);
}

export function renderPopup(payload: PopupPayload, ctx: RenderContext): string | null {
  if (payload.kind === 'wbgt') {
    const timeline = ctx.wbgt?.timeline;
    const ts = ctx.wbgt?.timestamp;
    if (!timeline || !ts) return null;
    const station = timeline.stations.find((s) => s.stationCode === payload.stationCode);
    if (!station) return null;
    const sample = station.samples.find((x) => x.observedAt === ts);
    if (!sample) {
      return wrap(`
        <p class="text-[10px] text-slate-500">${escape(station.prefecture)}</p>
        <p class="text-sm font-semibold text-slate-900">${escape(station.stationName)}</p>
        <p class="mt-1 text-xs text-slate-500">この時刻のデータなし</p>
      `);
    }
    const band = bandForLevel(sample.level);
    return wrap(`
      <p class="text-[10px] text-slate-500">${escape(station.prefecture)}</p>
      <p class="text-sm font-semibold text-slate-900">${escape(station.stationName)}</p>
      <div class="mt-1.5 flex items-baseline gap-2">
        <span class="inline-flex items-center rounded px-2 py-0.5 text-lg font-bold text-white" style="background-color:${band.color}">${sample.value.toFixed(1)}</span>
        <span class="text-xs font-medium" style="color:${band.color}">${escape(band.label)}</span>
      </div>
      <p class="mt-1 text-[10px] text-slate-500">${escape(formatJstDateHour(sample.observedAt))}</p>
    `);
  }
  const forecasts = ctx.forecast?.forecasts ?? [];
  const date = ctx.forecast?.date;
  const f = forecasts.find((x) => x.areaCode === payload.spotCode);
  if (!f || !date) return null;
  const day = f.daily.find((d) => d.date === date);
  if (!day) {
    return wrap(`
      <p class="text-[10px] text-slate-500">${escape(f.prefecture ?? '')}</p>
      <p class="text-sm font-semibold text-slate-900">${escape(f.areaName)}</p>
      <p class="mt-1 text-xs text-slate-500">この日のデータなし</p>
    `);
  }
  const tmaxStr = day.tmax != null ? `${Math.round(day.tmax)}°` : '–';
  return wrap(`
    <p class="text-[10px] text-slate-500">${escape(f.prefecture ?? '')}</p>
    <p class="text-sm font-semibold text-slate-900">${escape(f.areaName)}</p>
    <div class="mt-1.5 flex items-baseline gap-2">
      <span class="inline-flex items-center rounded px-2.5 py-1 text-xl font-bold text-white" style="background-color:${tempColor(day.tmax)}">${tmaxStr}</span>
      <span class="text-xs text-slate-700">最高気温</span>
    </div>
    <p class="mt-1.5 text-[10px] text-slate-500">${escape(formatJstDate(day.date))}</p>
  `);
}

function wrap(inner: string): string {
  return `<div class="px-2 py-1 min-w-[150px]">${inner}</div>`;
}

function formatJstDate(iso: string): string {
  try {
    const d = new Date(iso);
    if (Number.isNaN(d.getTime())) return iso;
    return new Intl.DateTimeFormat('ja-JP', {
      timeZone: 'Asia/Tokyo',
      month: 'numeric',
      day: 'numeric',
      weekday: 'short',
    }).format(d);
  } catch {
    return iso;
  }
}

function formatJstDateHour(iso: string): string {
  try {
    const d = new Date(iso);
    if (Number.isNaN(d.getTime())) return iso;
    return new Intl.DateTimeFormat('ja-JP', {
      timeZone: 'Asia/Tokyo',
      month: 'numeric',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    }).format(d);
  } catch {
    return iso;
  }
}
